import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { FaGoogle, FaStar, FaStarHalfAlt } from 'react-icons/fa';

const { FiTruck, FiShield, FiClock, FiMapPin, FiPhone, FiCheckCircle, FiUsers, FiCpu, FiRefreshCw, FiExternalLink, FiArrowRight, FiBell, FiClipboard } = FiIcons;

const Home = () => {
  const services = [
    {
      icon: FiCheckCircle,
      title: "Clean Truck Check",
      description: "Official CARB Clean Truck Checks and compliance testing to keep your fleet regulatory compliant."
    },
    {
      icon: FiTruck,
      title: "90-Day BIT Inspection",
      description: "Mobile 90-Day BIT inspections to keep your fleet safe and compliant with CHP regulations."
    },
    {
      icon: FiClipboard,
      title: "DOT Annual Inspection",
      description: "Complete mobile DOT Annual Inspections required by the FMCSA for all commercial motor vehicles."
    },
    {
      icon: FiCpu,
      title: "Diagnostics Services",
      description: "Advanced computer diagnostics to identify and resolve emissions system issues quickly."
    },
    {
      icon: FiRefreshCw,
      title: "Force Regens",
      description: "Professional forced regeneration services to clear DPF filters and restore engine performance."
    },
    {
      icon: FiClock,
      title: "Mobile Service",
      description: "We come to you! On-site service at your location for maximum convenience."
    }
  ];

  const cities = [
    "Ontario", "Rancho Cucamonga", "Upland", "Fontana", 
    "Chino", "Pomona", "San Bernardino", "Riverside"
  ];

  const reviews = [
    {
      name: "Carlos Rivera",
      date: "2 weeks ago",
      rating: 5,
      text: "Best mobile service in Ontario! They came out to our yard same-day and got our fleet compliant with the new CARB regulations. Professional, knowledgeable, and fair pricing.",
      initial: "C"
    },
    {
      name: "J&S Logistics",
      date: "1 month ago",
      rating: 5,
      text: "Ramirez Mobile Clean Truck is a lifesaver. We had a truck down with DPF issues and they performed a force regen and diagnostics right on site. Saved us a tow bill and downtime.",
      initial: "J"
    },
    {
      name: "Mike Thompson",
      date: "3 months ago",
      rating: 5,
      text: "Highly recommend for Clean Truck Checks. The technician was thorough and explained everything clearly. Will definitely use them for all our annual inspections.",
      initial: "M"
    }
  ];

  const galleryImages = [
    {
      url: "https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/76f068f9e1e56b57a7d668937e51e748.jpeg",
      caption: "Computer Diagnostics"
    },
    {
      url: "https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/f5e298b7d2339818def7142c2f1a2bf6.jpeg",
      caption: "Mobile Fleet Service"
    },
    {
      url: "https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/5b12c1751e7656c8707101b12103a2dc.jpg",
      caption: "Compliance Testing"
    },
    {
      url: "https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/0fe781cd8baaece893d6a01e4b4324a9.jpeg",
      caption: "Fleet Maintenance"
    }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section className="relative bg-gray-900 text-white py-24 lg:py-32 overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/a728ddab7d71aca898b8aa1e2ed2cb3f.jpeg" 
            alt="Commercial Truck Service" 
            className="w-full h-full object-cover opacity-40" 
          />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 to-gray-800/60"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              Stay CARB Compliant with Trusted <span className="text-yellow-400">Clean Truck Check</span> Services
            </motion.h1>
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl mb-8 text-gray-300 leading-relaxed"
            >
              At Ramirez Mobile Clean Truck, we specialize in helping commercial truck owners and fleet operators meet California Air Resources Board (CARB) compliance requirements. Whether you’re navigating clean truck checks, emissions regulations, or vehicle inspections, our expert services are designed to keep you on the road and in full regulatory compliance.
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mb-8 font-semibold text-yellow-300 text-lg"
            >
              Stay ahead of CARB enforcement and avoid costly penalties with our reliable, professional, and efficient solutions.
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            >
              <a 
                href="tel:9092842738" 
                className="bg-yellow-500 text-gray-900 font-bold px-8 py-4 rounded-lg hover:bg-yellow-400 transition-colors text-lg flex items-center space-x-2 shadow-lg"
              >
                <SafeIcon icon={FiPhone} />
                <span>Call (909) 284-2738</span>
              </a>
              <Link 
                to="/services" 
                className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white hover:text-gray-900 transition-colors text-lg shadow-lg"
              >
                View Services
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Reminder CTA Section */}
      <section className="bg-yellow-500 py-12">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between bg-gray-800 rounded-xl p-8 shadow-2xl relative overflow-hidden">
            {/* Decorative Circle */}
            <div className="absolute -right-10 -top-10 w-40 h-40 bg-gray-700 rounded-full opacity-50"></div>
            
            <div className="relative z-10 flex flex-col md:flex-row items-center gap-6 text-center md:text-left">
              <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center shrink-0">
                <SafeIcon icon={FiBell} className="text-3xl text-gray-900" />
              </div>
              <div>
                <h3 className="text-2xl font-bold text-white mb-2">Never Pay a Late Fine Again!</h3>
                <p className="text-gray-300 max-w-xl">
                  Sign up for our free automated reminder service. We'll track your fleet's compliance dates and notify you before inspections are due.
                </p>
              </div>
            </div>
            <Link 
              to="/reminders" 
              className="relative z-10 mt-6 md:mt-0 bg-white text-gray-900 font-bold px-8 py-3 rounded-lg hover:bg-gray-100 transition-colors whitespace-nowrap shadow-lg"
            >
              Get Free Reminders
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Comprehensive Fleet Services
            </h2>
            <h3 className="text-2xl text-gray-400 mb-8">
              Services We Provide
            </h3>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto">
              We provide a full suite of services to ensure your diesel trucks perform optimally and remain compliant with California's strict emissions standards.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-gray-800 p-8 rounded-lg hover:shadow-xl transition-shadow border-t-4 border-blue-500"
              >
                <SafeIcon icon={service.icon} className="text-4xl text-blue-500 mb-4 mx-auto block" />
                <h4 className="text-xl font-semibold text-white mb-4 text-center">
                  {service.title}
                </h4>
                <p className="text-gray-400 text-center">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Google Reviews Section */}
      <section className="py-20 bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center mb-12">
            <div className="bg-gray-700 p-4 rounded-full shadow-md mb-6">
              <SafeIcon icon={FaGoogle} className="text-4xl text-white" />
            </div>
            <h2 className="text-4xl font-bold text-white mb-4 text-center">
              Top Rated on Google
            </h2>
            <div className="flex items-center space-x-2 mb-4">
              <span className="text-3xl font-bold text-white">5.0</span>
              <div className="flex text-yellow-400 text-2xl">
                {[...Array(5)].map((_, i) => <SafeIcon key={i} icon={FaStar} />)}
              </div>
            </div>
            <a 
              href="https://share.google/Sc8FJKKx8YahKDjBC" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-blue-400 font-semibold hover:underline flex items-center gap-2"
            >
              See all reviews on Google
              <SafeIcon icon={FiExternalLink} className="text-sm" />
            </a>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {reviews.map((review, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-700 p-8 rounded-xl shadow-md border border-gray-600 relative"
              >
                <div className="absolute top-8 right-8 text-gray-500">
                  <SafeIcon icon={FaGoogle} className="text-2xl" />
                </div>
                <div className="flex items-center space-x-4 mb-4">
                  <div className="w-12 h-12 bg-gray-600 rounded-full flex items-center justify-center text-blue-400 font-bold text-xl">
                    {review.initial}
                  </div>
                  <div>
                    <h4 className="font-bold text-white">{review.name}</h4>
                    <p className="text-xs text-gray-400">{review.date}</p>
                  </div>
                </div>
                <div className="flex text-yellow-400 text-sm mb-4">
                  {[...Array(review.rating)].map((_, i) => <SafeIcon key={i} icon={FaStar} />)}
                </div>
                <p className="text-gray-300 text-sm leading-relaxed">
                  "{review.text}"
                </p>
              </motion.div>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <a 
              href="https://share.google/Sc8FJKKx8YahKDjBC" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="bg-transparent text-blue-400 border border-blue-400 px-8 py-3 rounded-lg hover:bg-blue-900/50 transition-colors font-semibold inline-flex items-center space-x-2"
            >
              <SafeIcon icon={FiIcons.FiMessageSquare} />
              <span>Leave a Review</span>
            </a>
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Our Work in Action
            </h2>
            <h3 className="text-2xl text-gray-400 mb-8">
              Professional Equipment & Expert Technicians
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {galleryImages.map((img, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group relative h-64 rounded-lg overflow-hidden shadow-lg cursor-pointer border border-gray-800"
              >
                <img 
                  src={img.url} 
                  alt={img.caption} 
                  className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-500" 
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                  <p className="text-white font-bold text-lg">{img.caption}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link to="/about" className="text-blue-400 font-bold hover:underline inline-flex items-center space-x-2">
              <span>Learn more about our capabilities</span>
              <SafeIcon icon={FiArrowRight} />
            </Link>
          </div>
        </div>
      </section>

      {/* Intro Feature Section */}
      <section className="py-20 bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2">
              <div className="relative rounded-xl overflow-hidden shadow-2xl border border-gray-700">
                <img 
                  src="https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/76f068f9e1e56b57a7d668937e51e748.jpeg" 
                  alt="Mechanic inspecting truck engine" 
                  className="w-full h-auto object-cover" 
                />
                <div className="absolute bottom-0 left-0 bg-blue-600 text-white px-6 py-2 rounded-tr-lg font-bold">
                  Expert Diagnostics
                </div>
              </div>
            </div>
            <div className="lg:w-1/2">
              <h2 className="text-3xl font-bold text-white mb-6">
                Professional Diagnostics & Compliance
              </h2>
              <p className="text-lg text-gray-300 mb-6 leading-relaxed">
                Modern diesel engines require sophisticated equipment and knowledgeable technicians. Our team is equipped with the latest diagnostic tools to accurately identify issues with your emissions system, DPF, DEF, and engine performance.
              </p>
              <p className="text-lg text-gray-300 mb-8 leading-relaxed">
                Don't let a check engine light or compliance deadline ground your fleet. We bring the shop to you, saving you time and towing costs.
              </p>
              <Link to="/about" className="text-blue-400 font-bold hover:text-blue-300 inline-flex items-center space-x-2 text-lg">
                <span>Learn More About Our Team</span>
                <SafeIcon icon={FiArrowRight} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-white mb-4">
              Service Areas in Southern California
            </h2>
            <h3 className="text-2xl text-gray-400 mb-8">
              Mobile CARB Compliance Services Throughout San Bernardino County
            </h3>
            <a 
              href="https://share.google/3y2ABFaMxbdz6hzaZ" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-blue-400 font-bold hover:underline text-lg"
            >
              <SafeIcon icon={FiMapPin} />
              <span>View Service Map on Google</span>
              <SafeIcon icon={FiExternalLink} className="text-sm" />
            </a>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            {cities.map((city, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link 
                  to={`/${city.toLowerCase().replace(' ', '-')}-ca`}
                  className="block bg-gray-800 p-6 rounded-lg shadow-md hover:shadow-lg transition-shadow text-center border border-gray-700 group"
                >
                  <SafeIcon icon={FiMapPin} className="text-2xl text-blue-500 mb-2 mx-auto block group-hover:scale-110 transition-transform" />
                  <h4 className="font-semibold text-white">{city}</h4>
                  <p className="text-sm text-gray-400">California</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <h2 className="text-4xl font-bold text-white mb-6">
                Why Choose Ramirez Mobile Clean Truck?
              </h2>
              <h3 className="text-2xl text-gray-400 mb-8">
                Ontario's Most Trusted CARB Compliance Specialists
              </h3>
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiCheckCircle} className="text-2xl text-green-500 mt-1" />
                  <div>
                    <h4 className="text-xl font-semibold text-white mb-2">Experienced Specialists</h4>
                    <p className="text-gray-300">Expert CARB compliance specialists with years of experience.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiClock} className="text-2xl text-green-500 mt-1" />
                  <div>
                    <h4 className="text-xl font-semibold text-white mb-2">Same-Day Service</h4>
                    <p className="text-gray-300">Emergency and same-day service available throughout our service areas.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiTruck} className="text-2xl text-green-500 mt-1" />
                  <div>
                    <h4 className="text-xl font-semibold text-white mb-2">Mobile Service</h4>
                    <p className="text-gray-300">We come to your location - no need to take your trucks out of service.</p>
                  </div>
                </div>
                <div className="flex items-start space-x-4">
                  <SafeIcon icon={FiUsers} className="text-2xl text-green-500 mt-1" />
                  <div>
                    <h4 className="text-xl font-semibold text-white mb-2">Fleet Specialists</h4>
                    <p className="text-gray-300">Comprehensive fleet management and compliance tracking services.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="order-1 lg:order-2">
              <div className="relative rounded-xl overflow-hidden shadow-2xl mb-8 border border-gray-700">
                <img 
                  src="https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/0fe781cd8baaece893d6a01e4b4324a9.jpeg" 
                  alt="Logistics fleet" 
                  className="w-full h-auto object-cover" 
                />
              </div>
              <div className="bg-blue-600 p-8 rounded-lg text-white shadow-xl">
                <h3 className="text-2xl font-bold mb-4">Get Your Free Quote Today</h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiPhone} className="text-xl text-yellow-300" />
                    <div>
                      <p className="font-semibold text-blue-100">Call Now</p>
                      <a href="tel:9092842738" className="text-white text-2xl font-bold hover:text-yellow-300 transition-colors">
                        (909) 284-2738
                      </a>
                    </div>
                  </div>
                  <Link 
                    to="/contact" 
                    className="block w-full bg-white text-gray-900 text-center py-3 rounded-lg hover:bg-yellow-400 transition-colors font-bold mt-4"
                  >
                    Request Quote Online
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 bg-gray-900 text-white overflow-hidden border-t border-gray-800">
        <div className="absolute inset-0 z-0 opacity-20">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/0fbf60f8fa242397e9ef833782db8ed93045df54e3b36d72437cb9900e44cd21f118b78650ffb5dd82aaaddf0850c39a/images/aW1hZ2UvanBlZw==/0fe781cd8baaece893d6a01e4b4324a9.jpeg" 
            alt="Highway at night" 
            className="w-full h-full object-cover" 
          />
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-4">
            Need CARB Compliance Service Today?
          </h2>
          <h3 className="text-2xl mb-8">
            Emergency Service Available - Call Now!
          </h3>
          <p className="text-xl mb-8 text-gray-300">
            Don't risk fines or downtime. Our mobile service comes to you in Ontario, CA and surrounding areas.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="tel:9092842738" 
              className="bg-yellow-500 text-gray-900 font-bold px-8 py-4 rounded-lg hover:bg-yellow-400 transition-colors text-lg"
            >
              Call (909) 284-2738 Now
            </a>
            <Link 
              to="/contact" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg hover:bg-white hover:text-gray-900 transition-colors text-lg"
            >
              Schedule Service Online
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;